import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { Upload, CheckCircle2, AlertTriangle, FileText, Loader2, ShieldAlert, X, History, ChevronDown, ChevronUp } from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";

/**
 * RawDnaUploadCard — iter706 / Phase 3.
 *
 * Позволява на потребителя да качи raw 23andMe / AncestryDNA `.txt` файл.
 * Файлът се парсира локално на бекенда; идентифицират се само nutrition-
 * relevant SNP-и от whitelist; резултатите се вписват в lab_entries и
 * директно подхранват GeneticNutritionSection.
 *
 * Строго EDUCATIONAL — без PRS, без BRCA1/2, без lifetime risk %.
 */
const TX = {
  bg: {
    title:          "Качване на raw DNA файл",
    subtitle:       "Поддържат се 23andMe и AncestryDNA `.txt` exports (до 50 MB).",
    drop:           "Пуснете файла тук или",
    browse:         "изберете файл",
    uploading:      "Обработка…",
    successTitle:   "Успешно идентифицирани варианти",
    errorTitle:     "Грешка при качване",
    snpsFound:      "идентифицирани варианти",
    linesScanned:   "сканирани реда",
    skippedRsid:    "извън whitelist",
    skippedNoCall:  "no-call",
    skippedStrand:  "strand mismatch",
    privacyTitle:   "Поверителност и обхват",
    privacy1:       "Парсваме само образователен whitelist от nutrition-relevant SNP-и (MTHFR, COMT, VDR, BCMO1, FTO, TCF7L2, PPARG).",
    privacy2:       "BRCA1 / BRCA2 НЕ се обработват — те изискват CLIA-сертифицирана лаборатория и онкогенетична консултация.",
    privacy3:       "НЕ изчисляваме polygenic risk score (PRS) или lifetime risk %. Това е изключително образователен extract.",
    privacy4:       "Файлът се обработва веднъж и не се съхранява в оригинален вид — само whitelisted генотипове отиват в lab_entries.",
    consent:        "Разбирам и съгласявам се",
    show:           "Покажи поддържани rsID",
    hide:           "Скрий",
    close:          "Затвори",
    historyTitle:   "Предишни качвания",
    rsidLabel:      "rsID",
    geneLabel:      "Ген / вариант",
    alleleLabel:    "Алели",
    genoLabel:      "Генотип",
  },
  en: {
    title:          "Upload raw DNA file",
    subtitle:       "Supports 23andMe / AncestryDNA `.txt` exports up to 50 MB.",
    drop:           "Drop a file here or",
    browse:         "browse",
    uploading:      "Processing…",
    successTitle:   "Variants identified",
    errorTitle:     "Upload failed",
    snpsFound:      "variants identified",
    linesScanned:   "lines scanned",
    skippedRsid:    "off-whitelist",
    skippedNoCall:  "no-call",
    skippedStrand:  "strand mismatch",
    privacyTitle:   "Privacy & Scope",
    privacy1:       "We parse a small educational whitelist of nutrition-relevant SNPs only.",
    privacy2:       "BRCA1 / BRCA2 are NOT processed — clinical genotyping is required.",
    privacy3:       "We do NOT compute PRS or lifetime risk %. This is an educational extract.",
    privacy4:       "The raw file is parsed in-flight; only whitelisted genotypes are persisted.",
    consent:        "I understand and agree",
    show:           "Show supported rsIDs",
    hide:           "Hide",
    close:          "Close",
    historyTitle:   "Previous uploads",
    rsidLabel:      "rsID",
    geneLabel:      "Gene / variant",
    alleleLabel:    "Alleles",
    genoLabel:      "Genotype",
  },
};

export default function RawDnaUploadCard({ lang = "bg", onUploaded }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const t = TX[lang] || TX.bg;

  const [consented, setConsented]   = useState(false);
  const [dragOver, setDragOver]     = useState(false);
  const [uploading, setUploading]   = useState(false);
  const [result, setResult]         = useState(null);
  const [error, setError]           = useState(null);
  const [showRsids, setShowRsids]   = useState(false);
  const [rsidList, setRsidList]     = useState(null);
  const [uploadHistory, setUploadHistory] = useState(null);   // iter756
  const [showHistory, setShowHistory] = useState(false);
  const inputRef = useRef(null);

  // iter756 — load DNA upload history
  const loadHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/breast-intelligence/dna/uploads`);
      setUploadHistory(data.uploads || []);
    } catch { setUploadHistory([]); }
  }, [API]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const cardCls = isDark
    ? "bg-[hsl(222,47%,11%)] border-[hsl(223,47%,18%)]"
    : "bg-white border-gray-200";

  const dropCls = `${isDark ? "border-white/20 hover:border-violet-400" : "border-gray-300 hover:border-violet-500"} ${
    dragOver ? (isDark ? "bg-violet-500/10 border-violet-400" : "bg-violet-50 border-violet-500") : ""
  }`;

  const loadRsids = async () => {
    if (rsidList) { setShowRsids((v) => !v); return; }
    try {
      const { data } = await axios.get(`${API}/breast-intelligence/dna/supported-rsids`);
      setRsidList(data.rsids || []);
      setShowRsids(true);
    } catch (_) { /* ignore */ }
  };

  const doUpload = async (file) => {
    if (!file) return;
    setError(null);
    setResult(null);
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(
        `${API}/breast-intelligence/dna/upload-raw`,
        form,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 120_000 },
      );
      setResult(data);
      if (onUploaded) onUploaded(data);
      loadHistory();  // iter756 — refresh history
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Unknown error");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (!consented) return;
    const file = e.dataTransfer?.files?.[0];
    if (file) doUpload(file);
  };

  return (
    <section className="space-y-3" data-testid="raw-dna-upload-card">
      <div className="flex items-center gap-2 flex-wrap">
        <Upload className="w-5 h-5 text-violet-500" />
        <h2 className="text-lg sm:text-xl font-bold">{t.title}</h2>
      </div>
      <p className="text-xs opacity-60">{t.subtitle}</p>

      {/* Privacy / consent block */}
      <div
        className={`${cardCls} rounded-2xl border p-4 space-y-2`}
        data-testid="raw-dna-privacy"
      >
        <div className="flex items-center gap-2 font-semibold text-sm">
          <ShieldAlert className="w-4 h-4 text-amber-500" />
          {t.privacyTitle}
        </div>
        <ul className="text-xs opacity-80 space-y-1.5 list-disc list-inside leading-relaxed">
          <li>{t.privacy1}</li>
          <li>{t.privacy2}</li>
          <li>{t.privacy3}</li>
          <li>{t.privacy4}</li>
        </ul>
        <label className="flex items-center gap-2 text-sm pt-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={consented}
            onChange={(e) => setConsented(e.target.checked)}
            className="accent-violet-500"
            data-testid="raw-dna-consent"
          />
          {t.consent}
        </label>
      </div>

      {/* Drop zone */}
      <div
        className={`${cardCls} rounded-2xl border-2 border-dashed p-8 text-center transition ${dropCls}`}
        onDragOver={(e) => { e.preventDefault(); if (consented) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        data-testid="raw-dna-dropzone"
      >
        {uploading ? (
          <div className="flex items-center justify-center gap-2 opacity-80">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>{t.uploading}</span>
          </div>
        ) : (
          <>
            <Upload className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <div className="text-sm">
              {t.drop}{" "}
              <button
                type="button"
                disabled={!consented}
                onClick={() => inputRef.current?.click()}
                className={`underline font-semibold ${consented ? "text-violet-600 dark:text-violet-300 cursor-pointer" : "opacity-40 cursor-not-allowed"}`}
                data-testid="raw-dna-browse"
              >
                {t.browse}
              </button>
            </div>
            <input
              ref={inputRef}
              type="file"
              accept=".txt,.tsv,.csv,text/plain,text/csv"
              className="hidden"
              onChange={(e) => doUpload(e.target.files?.[0])}
              data-testid="raw-dna-input"
            />
          </>
        )}
      </div>

      {/* Error */}
      {error && (
        <div
          className={`rounded-2xl border p-4 flex items-start gap-3 ${isDark ? "bg-red-500/10 border-red-500/40 text-red-200" : "bg-red-50 border-red-200 text-red-800"}`}
          data-testid="raw-dna-error"
        >
          <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <div className="font-semibold">{t.errorTitle}</div>
            <div className="opacity-80">{String(error)}</div>
          </div>
          <button onClick={() => setError(null)} className="opacity-60 hover:opacity-100">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Success */}
      {result && (
        <div className={`${cardCls} rounded-2xl border p-4 space-y-3`} data-testid="raw-dna-result">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            <div className="font-semibold">{t.successTitle}</div>
            <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full ${isDark ? "bg-violet-500/20 text-violet-300" : "bg-violet-100 text-violet-700"}`}>
              {result.vendor}
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <Stat k={result.snps_identified} v={t.snpsFound} isDark={isDark} highlight />
            <Stat k={result.total_lines_seen} v={t.linesScanned} isDark={isDark} />
            <Stat k={result.skipped_unknown_rsid} v={t.skippedRsid} isDark={isDark} />
            <Stat k={result.skipped_no_call + result.skipped_strand_mismatch}
                  v={`${t.skippedNoCall} / ${t.skippedStrand}`} isDark={isDark} />
          </div>

          {result.variants_preview && result.variants_preview.length > 0 && (
            <div className="overflow-x-auto rounded-lg border border-white/5">
              <table className="min-w-full text-xs">
                <thead className={isDark ? "bg-white/5" : "bg-gray-50"}>
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">{t.rsidLabel}</th>
                    <th className="px-3 py-2 text-left font-semibold">{t.geneLabel}</th>
                    <th className="px-3 py-2 text-left font-semibold">{t.alleleLabel}</th>
                    <th className="px-3 py-2 text-left font-semibold">{t.genoLabel}</th>
                  </tr>
                </thead>
                <tbody>
                  {result.variants_preview.map((v) => (
                    <tr key={v.rsid} className={isDark ? "border-t border-white/5" : "border-t border-gray-100"}>
                      <td className="px-3 py-2 font-mono">{v.rsid}</td>
                      <td className="px-3 py-2">{v.label_bg}</td>
                      <td className="px-3 py-2 font-mono">{v.raw_alleles}</td>
                      <td className="px-3 py-2 font-mono opacity-80">{v.genotype_key}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {result.disclaimer_bg && (
            <p className="text-[11px] opacity-60 italic leading-relaxed">
              {result.disclaimer_bg}
            </p>
          )}
        </div>
      )}

      {/* Supported rsID list */}
      <div>
        <button
          type="button"
          onClick={loadRsids}
          className="text-xs opacity-70 hover:opacity-100 underline"
          data-testid="raw-dna-show-rsids"
        >
          {showRsids ? t.hide : t.show}
        </button>
        {showRsids && rsidList && (
          <div className={`${cardCls} rounded-xl border mt-2 p-3`}>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
              {rsidList.map((r) => (
                <div key={r.rsid} className="flex items-center gap-2 truncate">
                  <FileText className="w-3 h-3 opacity-50 shrink-0" />
                  <span className="font-mono">{r.rsid}</span>
                  <span className="opacity-60 truncate">{r.label_bg}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* iter756 — DNA upload history */}
        {uploadHistory && uploadHistory.length > 0 && (
          <div
            data-testid="dna-upload-history"
            className={`rounded-lg mt-3 border ${
              isDark ? "border-white/10 bg-white/5" : "border-gray-200 bg-gray-50"
            }`}
          >
            <button
              type="button"
              onClick={() => setShowHistory((v) => !v)}
              data-testid="dna-upload-history-toggle"
              className="w-full px-3 py-2 flex items-center justify-between"
            >
              <span className={`flex items-center gap-1.5 text-[11px] font-semibold ${isDark ? "text-slate-200" : "text-slate-700"}`}>
                <History className="w-3.5 h-3.5" />
                {lang === "bg" ? "История на качванията" : "Upload history"}
                <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold ${
                  isDark ? "bg-violet-500/40 text-violet-100" : "bg-violet-100 text-violet-800"
                }`}>
                  {uploadHistory.length}
                </span>
              </span>
              {showHistory ? <ChevronUp className="w-3.5 h-3.5 opacity-60" /> : <ChevronDown className="w-3.5 h-3.5 opacity-60" />}
            </button>
            {showHistory && (
              <div className={`px-3 pb-3 pt-0 border-t space-y-1.5 ${isDark ? "border-white/10" : "border-gray-200"}`}>
                {uploadHistory.map((up) => {
                  const d = new Date(up.uploaded_at || Date.now());
                  const dateStr = d.toLocaleString(lang === "bg" ? "bg-BG" : "en-US", {
                    year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                  });
                  return (
                    <div
                      key={up._id}
                      data-testid={`dna-upload-item-${up._id}`}
                      className={`p-2 rounded text-[11px] flex items-center justify-between gap-2 ${
                        isDark ? "bg-slate-950/60" : "bg-white"
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <FileText className="w-3 h-3 opacity-60" />
                          <span className={`font-mono text-[10px] font-semibold ${isDark ? "text-violet-300" : "text-violet-700"}`}>
                            {up.source || "DNA"}
                          </span>
                          <span className="opacity-70 text-[10px]">
                            {up.filename || (lang === "bg" ? "неозаглавен" : "unnamed")}
                          </span>
                        </div>
                        <div className={`text-[10px] mt-0.5 opacity-70`}>{dateStr}</div>
                      </div>
                      <div className={`text-[11px] font-mono tabular-nums ${
                        isDark ? "text-emerald-300" : "text-emerald-700"
                      }`}>
                        {up.rsids_extracted ?? up.rsid_count ?? 0} rSIDs
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function Stat({ k, v, isDark, highlight }) {
  return (
    <div
      className={`rounded-lg p-2 text-center ${
        highlight
          ? (isDark ? "bg-violet-500/15 text-violet-200" : "bg-violet-50 text-violet-700")
          : (isDark ? "bg-white/5" : "bg-gray-50")
      }`}
    >
      <div className="font-bold text-base">{k ?? 0}</div>
      <div className="opacity-70 text-[10px] leading-snug">{v}</div>
    </div>
  );
}
